import { FormSelect, FormTextarea, FormInput, FormDatePicker } from "@/components";
import { Button } from "@/components/ui";
import { Bookmark } from "lucide-react";
import { FC } from "react";
import { Controller, FormProvider } from "react-hook-form";
import {
  COMMENT,
  TARGET_DATE,
  NO_OF_WORKSTATIONS,
  SPECIAL_REQUIREMENTS,
  LAB_SETUP_STATUS,
  labSetupStatusOptions,
} from "./consts";
import { useAddEditLabSetup } from "./useAddEditLabSetup";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { LOGIN_ROLES } from "@/lib/utils";

const AddEditResource: FC = () => {
  const {
    form,
    onSubmit,
    isSubmitting,
    isLoadingExisting,
  } = useAddEditLabSetup();

  const { handleSubmit, control, formState } = form;
  const { errors } = formState;
  const loggedInUserData = useSelector(
      (state: RootState) => state.currentData.loggedInUserData
    );
  
    // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
    const roles = loggedInUserData?.role || [];
  
    // Helper to check if user has a given role
    const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);
  return (
    <FormProvider {...form}>
      {isLoadingExisting ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading existing data...</div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit((data) => onSubmit(data as any))}
          className="grid gap-4 mt-10"
        >
          {/* Relationship Manager Section */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] text-primary mt-5">Lab Setup Details</h2>
            {(hasRole(LOGIN_ROLES.SALES_OPS) ||
              hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
              hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) ||
              hasRole(LOGIN_ROLES.LABS_TEAM)) && (
              <Button type="submit" variant="default" disabled={isSubmitting}>
                Save
              </Button>
            )}
          </div>

          {/* Relationship Manager Section */}
          <div className="space-y-4">
            {/* New Lab Fields: Target Date, No of workstations, Lab Setup Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormDatePicker
                control={control}
                name={TARGET_DATE}
                label="Target Date"
                placeholder="Select target date"
                error={errors[TARGET_DATE]?.message as string}
                isRequired
                minDate={new Date()}
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM)
                  )
                }
              />

              <FormInput
                control={control}
                name={NO_OF_WORKSTATIONS}
                label="Number of Workstations"
                placeholder="Enter number of workstations"
                error={errors[NO_OF_WORKSTATIONS]?.message as string}
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN)
                  )
                }
              />

              <FormSelect
                name={LAB_SETUP_STATUS}
                control={control}
                label="Lab setup Status"
                placeholder="Please select"
                error={errors[LAB_SETUP_STATUS]?.message as string}
                options={labSetupStatusOptions}
                isRequired
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM)
                  )
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
               <FormTextarea
                name={SPECIAL_REQUIREMENTS}
                control={control}
                label="Special Requirements"
                placeholder="Enter any special requirements"
                error={errors[SPECIAL_REQUIREMENTS]?.message as string}
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM)
                  )
                }
              />
              <FormTextarea
                name={COMMENT}
                control={control}
                label="Comments"
                placeholder="Additional comments"
                error={errors[COMMENT]?.message as string}
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) ||
                    hasRole(LOGIN_ROLES.LABS_TEAM)
                  )
                }
              />
            </div>
          </div>
        </form>
      )}
    </FormProvider>
  );
};

export default AddEditResource;
